import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, first, Observable, ReplaySubject } from 'rxjs';
import { DeferredPageInfo, PageInfoData, PageInfoTrackingService } from './pageinfo-tracking.service';

const EMPTY_PAGE_INFO_DATA: PageInfoData = { searchTerm: null, numSearchResults: null };

/**
 * Holds back the pageInfo event of the current navigation until the page reports itself as complete.
 *
 * Provide it on the component the router activates for a page, and inject it anywhere below that component
 * to report the page as complete:
 *
 * ```ts
 * @Component({ providers: [DeferredPageInfoTracking] })
 * export class ProductSearchRouteComponent { }
 *
 * // any descendant, created at any time
 * private readonly deferredPageInfoTracking: DeferredPageInfoTracking | null = inject(DeferredPageInfoTracking, { optional: true });
 * this.deferredPageInfoTracking?.complete({ searchTerm, numSearchResults });
 * ```
 *
 * A page that only redirects to itself to complete its URL reports the completed URL: the pageInfo of the
 * URL it redirects away from stays unreported and is replaced by the one of the redirected navigation.
 *
 * It has to exist within the turn that follows `NavigationEnd`, when tracking collects the pages that hold
 * their event back. Providing it on a routed component, or on a component rendered in that turn, is early
 * enough; a component that only exists after loading data of its own is too late and its page is reported
 * without its data.
 */
@Injectable()
export class DeferredPageInfoTracking implements DeferredPageInfo {
  private readonly router: Router = inject(Router);
  private readonly trackingService: PageInfoTrackingService = inject(PageInfoTrackingService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private completion$: ReplaySubject<PageInfoData> = new ReplaySubject<PageInfoData>(1);

  public constructor() {
    // The page reports itself once per navigation, so a report of the previous navigation must not be reused.
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.completion$ = new ReplaySubject<PageInfoData>(1);
    });

    this.trackingService.registerDeferredTracking(this);
    this.destroyRef.onDestroy(() => {
      this.trackingService.releaseDeferredTracking(this);
      this.completion$.next(EMPTY_PAGE_INFO_DATA);
    });
  }

  /** Reports the page as complete, with the pageInfo data it contributes. */
  public complete(data: PageInfoData = EMPTY_PAGE_INFO_DATA): void {
    this.completion$.next(data);
  }

  public whenCompleted$(): Observable<PageInfoData> {
    return this.completion$.pipe(first());
  }
}
